import {apiRequest, paginatedApiRequest} from './api';
import {BaseRequestPayload} from './api/types/api-request.types';
import {
  adminChallengeEndEndpoint,
  adminChallengeEndpoint,
  adminChallengePublishEndpoint,
  adminChallengesEndpoint,
  adminChallengeTemplateEndpoint,
  adminChallengeTemplatesEndpoint,
} from '../constants/endpoints';

export interface LocalizedText {
  en: string;
  ro: string;
  pt: string;
}

export type ChallengeStatus = 'Draft' | 'Published' | 'Ended';

export type ChallengeGoalType =
  | 'VisitNewPlaces'
  | 'VisitCategory'
  | 'VisitAnyOf'
  | 'PostMoods'
  | 'WriteReviews'
  | 'VisitWithFriend'
  | 'HonorBookings';

export interface AdminChallenge {
  id: string;
  kind: string;
  status: ChallengeStatus;
  title: LocalizedText;
  description: LocalizedText;
  goalType: ChallengeGoalType;
  target: number;
  goalTagCodes: number[];
  goalActivityIds: string[];
  rewardPoints: number;
  startsAt: string;
  endsAt: string;
  // Null means everywhere.
  locationCode: number | null;
  // Null means no limit.
  maxParticipants: number | null;
  participants: number;
  completed: number;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface SaveChallengeInput {
  title: LocalizedText;
  description: LocalizedText;
  goalType: ChallengeGoalType;
  target: number;
  goalTagCodes?: number[];
  goalActivityIds?: string[];
  rewardPoints: number;
  startsAt: string;
  endsAt: string;
  locationCode?: number | null;
  maxParticipants?: number | null;
}

export interface AdminChallengesPage {
  items: AdminChallenge[];
  totalItems: number;
  maxPage: number;
}

export interface ChallengeTemplate {
  id: number;
  enabled: boolean;
  goalType: ChallengeGoalType;
  // May contain {target}, replaced by the drawn target.
  title: LocalizedText;
  description: LocalizedText;
  targetMin: number;
  targetMax: number;
  rewardPoints: number;
  goalTagCodes: number[];
  weight: number;
  updatedAt: string;
}

export interface SaveChallengeTemplateInput {
  enabled: boolean;
  goalType: ChallengeGoalType;
  title: LocalizedText;
  description: LocalizedText;
  targetMin: number;
  targetMax: number;
  rewardPoints: number;
  goalTagCodes?: number[];
  weight: number;
}

// What the API's error codes mean to an admin.
const ERROR_MESSAGES: Record<string, string> = {
  invalid_text:
    'Every title (up to 120 characters) and description (up to 500) is required in English, Romanian and Portuguese.',
  invalid_goal: 'The goal type is not valid for this challenge.',
  invalid_numbers:
    'Check the numbers: target 1-100, reward 0-1000, max participants at least 1, weight 1-100.',
  invalid_dates:
    'The end must be after the start, and in the future when publishing.',
  missing_tags: 'Pick at least one tag for a "Visit a category" goal.',
  missing_activities:
    'Pick at least one place or event for a "Visit one of" goal.',
  ended: 'This challenge has ended and can no longer be changed.',
  not_draft: 'Only a draft can be published.',
  not_published: 'Only a published challenge can be ended.',
  max_below_participants:
    'Max participants cannot be below the number of users already joined.',
};

// apiRequest reports failures as "Server error: {code, message}", where code is
// the generic error.validation / error.notFound and message carries the semantic
// code (invalid_dates, not_draft, ...). This turns that code into a readable
// message, falling back to the server's own text.
const readableMessage = (error: unknown, fallback: string): string => {
  const text = error instanceof Error ? error.message : '';
  try {
    const parsed = JSON.parse(text.replace(/^Server error: /, ''));
    for (const key of [parsed?.message, parsed?.code]) {
      if (typeof key === 'string' && ERROR_MESSAGES[key]) {
        return ERROR_MESSAGES[key];
      }
    }
    if (typeof parsed?.message === 'string' && parsed.message) {
      return parsed.message;
    }
  } catch {
    // Not JSON: an HTTP status or a network failure.
  }
  return text ? `${fallback} (${text})` : fallback;
};

const request = async <TResponse, TBody = undefined>(
  payload: BaseRequestPayload<TBody>,
  failureMessage: string,
): Promise<TResponse> => {
  let failure: Error | null = null;

  const response = await apiRequest<TResponse, TBody>(payload, {
    errorMessage: failureMessage,
    onError: (error: unknown) => {
      failure = new Error(readableMessage(error, failureMessage));
    },
  });

  if (failure) {
    throw failure;
  }
  if (!response || response.data === undefined || response.data === null) {
    throw new Error(failureMessage);
  }
  return response.data;
};

const emptyPage: AdminChallengesPage = {items: [], totalItems: 0, maxPage: 0};

export const fetchAdminChallenges = async (filters: {
  status?: ChallengeStatus;
  pageNumber: number;
  pageSize: number;
}): Promise<AdminChallengesPage> => {
  const message = 'Failed to fetch challenges';
  let failure: Error | null = null;

  const response = await paginatedApiRequest<AdminChallenge[]>(
    {
      endpoint: adminChallengesEndpoint,
      method: 'GET',
      queryParams: {
        status: filters.status,
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize,
      },
    },
    {
      errorMessage: message,
      onError: (error: unknown) => {
        failure = new Error(readableMessage(error, message));
      },
    },
  );

  if (failure) {
    throw failure;
  }

  // An empty result does not come back as a paginated payload.
  if (!response) {
    return emptyPage;
  }

  return {
    items: response.data ?? [],
    totalItems: response.totalItems,
    maxPage: response.maxPage,
  };
};

export const createChallenge = (input: SaveChallengeInput) =>
  request<AdminChallenge, SaveChallengeInput>(
    {endpoint: adminChallengesEndpoint, method: 'POST', body: input},
    'Failed to create the challenge',
  );

export const updateChallenge = (id: string, input: SaveChallengeInput) =>
  request<AdminChallenge, SaveChallengeInput>(
    {endpoint: adminChallengeEndpoint(id), method: 'PUT', body: input},
    'Failed to save the challenge',
  );

export const publishChallenge = (id: string) =>
  request<AdminChallenge>(
    {endpoint: adminChallengePublishEndpoint(id), method: 'POST'},
    'Failed to publish the challenge',
  );

export const endChallenge = (id: string) =>
  request<AdminChallenge>(
    {endpoint: adminChallengeEndEndpoint(id), method: 'POST'},
    'Failed to end the challenge',
  );

export const fetchChallengeTemplates = (): Promise<ChallengeTemplate[]> =>
  request<ChallengeTemplate[]>(
    {endpoint: adminChallengeTemplatesEndpoint, method: 'GET'},
    'Failed to fetch the weekly templates',
  );

export const createChallengeTemplate = (input: SaveChallengeTemplateInput) =>
  request<ChallengeTemplate, SaveChallengeTemplateInput>(
    {endpoint: adminChallengeTemplatesEndpoint, method: 'POST', body: input},
    'Failed to create the template',
  );

export const updateChallengeTemplate = (
  id: number,
  input: SaveChallengeTemplateInput,
) =>
  request<ChallengeTemplate, SaveChallengeTemplateInput>(
    {endpoint: adminChallengeTemplateEndpoint(id), method: 'PUT', body: input},
    'Failed to save the template',
  );
